import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasModuleAccess } from '../services/payment';
import { Lock, Sparkles } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface PremiumRouteProps {
  children: React.ReactNode;
  moduleId: string; // Module identifier (e.g., 'cv-scanner', 'company-match', 'interview-prep')
  moduleName?: string; // Display name for the module
  redirectTo?: string; // Where to redirect if no access (default: /pricing)
}

export const PremiumRoute: React.FC<PremiumRouteProps> = ({
  children,
  moduleId,
  moduleName,
  redirectTo = '/pricing',
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      // Module 1 (cv-scanner) is always free - no authentication required
      if (moduleId === 'cv-scanner') {
        setHasAccess(true);
        setIsChecking(false);
        return;
      }

      if (!isAuthenticated || !user) {
        setIsChecking(false);
        setHasAccess(false);
        return;
      }

      // SUPERUSERS: Full access to all modules
      if (user.is_superuser) {
        setHasAccess(true);
        setIsChecking(false);
        return;
      }

      // FREE USERS: Only allow access to Module 1 (cv-scanner)
      // Block access to Modules 2, 3, 4 for free users
      if (!user.is_premium) {
        // Free users can only access Module 1
        setHasAccess(false);
        setIsChecking(false);
        return;
      }

      // PREMIUM USERS (Basic or Premium plan): Check module-specific access
      // Verify that the user has access to the specific module (pass user to avoid API call)
      const moduleAccess = await hasModuleAccess(moduleId, user);
      setHasAccess(moduleAccess);
      setIsChecking(false);
    };

    if (!isLoading) {
      checkAccess();
    }
  }, [isAuthenticated, user, isLoading, moduleId]);

  // Show loading while checking
  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Login Required | MotivaMatch</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-orange-100 p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-purple-600 text-white mb-6">
              <Lock className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Sign in required</h2>
            <p className="text-gray-600 mb-6">Please sign in to access this feature.</p>
            <button
              onClick={() => navigate('/auth', { state: { from: location }, replace: true })}
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Go to Login
            </button>
          </div>
        </div>
      </>
    );
  }

  // Show premium required message if no access
  if (!hasAccess) {
    return (
      <>
        <Helmet>
          <title>Premium Required - {moduleName || 'Feature'} | MotivaMatch</title>
          <meta name="description" content={`Access to ${moduleName || 'this feature'} requires a premium subscription.`} />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center px-4">
          <div className="max-w-xl w-full bg-white rounded-3xl shadow-xl border border-orange-100 p-8 md:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-purple-600 text-white mb-6">
              <Sparkles className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Premium Feature</h2>
            <p className="text-gray-600 mb-2">
              Access to <strong>{moduleName || 'this feature'}</strong> requires a premium subscription.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Upgrade to unlock this feature and get access to all premium modules.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate(redirectTo, { state: { moduleId, from: location.pathname } })}
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-purple-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                View Pricing
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-xl hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // User has access - show content
  return <>{children}</>;
};

